import { computed, reactive } from 'vue';
import type { ComputedRef } from 'vue';
import type { LocationQuery, LocationQueryValue } from 'vue-router';
import { useCategoriesState, useCategory, useCategoryContent } from '.';

const PRODUCTS_PER_PAGE = 30;

const CATEGORY_PARAMS = [
  'category',
  'subcategory',
  'subsubcategory',
  'subsubsubcategory',
] as const;

interface CategoryNode {
  slug: string;
  name?: string;
  children?: CategoryNode[];
  seo?: { page_title?: string; description?: string; og_title?: string };
  [key: string]: any;
}

interface CatalogChange {
  categories?: number[];
  features?: number[];
  page?: number;
}

interface CategoryFetchParams {
  page?: number;
  category_id?: number[];
  filters?: number[];
  includes: string[];
  'order[external_cf_published_at]': 'asc' | 'desc';
  'order[popular]': 'asc' | 'desc';
  limit: number;
  lang: string;
}

const singleQueryValue = (
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string | undefined => (typeof value === 'string' ? value : undefined);

const commaSeparatedIds = (
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): number[] | undefined => singleQueryValue(value)?.split(',').map(Number);

const pageNumber = (query: LocationQuery): number | undefined =>
  Number(singleQueryValue(query.page)) || undefined;

export default function useCategoryPage() {
  const route = useRoute();
  const router = useRouter();
  const { locale } = useI18n();
  const { categories: allCategories } = useCategoriesState();

  const slugs = CATEGORY_PARAMS.map((param) => route.params[param]).filter(
    (slug): slug is string => typeof slug === 'string' && !!slug,
  );

  if (!slugs.length) throw createError({ statusCode: 404, fatal: true });

  const chain: ComputedRef<CategoryNode[]> = computed(() =>
    slugs.reduce((resolved: CategoryNode[], slug, index) => {
      if (index && !resolved[index - 1]) return resolved;

      const level = index ? resolved[index - 1].children : allCategories.value;
      const found = level?.find((category) => category.slug === slug);

      return found ? [...resolved, found] : resolved;
    }, []),
  );

  const node = computed(() => chain.value[slugs.length - 1]);
  if (!node.value) throw createError({ statusCode: 404, fatal: true });

  useHead(() => ({
    title: node.value?.seo?.page_title,
    meta: [
      {
        name: 'description',
        key: 'description',
        content: node.value?.seo?.description,
      },
      {
        property: 'og:title',
        key: 'og:title',
        content: node.value?.seo?.og_title,
      },
    ],
  }));

  const fetchParams = reactive<CategoryFetchParams>({
    page: pageNumber(route.query),
    category_id: commaSeparatedIds(route.query.categories),
    filters: commaSeparatedIds(route.query.features),
    includes: ['variations'],
    'order[external_cf_published_at]': 'desc',
    'order[popular]': 'desc',
    limit: PRODUCTS_PER_PAGE,
    lang: locale.value,
  });

  const { fetch } = useCategory(slugs[slugs.length - 1], fetchParams);

  const {
    data,
    pending: loadingProducts,
    refresh,
  } = useLazyAsyncData(
    `product-category-${slugs.join('-')}-${locale.value}`,
    () => fetch(),
    {
      default: () => ({ category: undefined, pagesCount: 0 }),
    },
  );

  const categoryData = computed(() => data.value.category);
  const totalPages = computed(() => data.value.pagesCount);
  const { categoryProducts: products } = useCategoryContent(categoryData);

  const findSection = (type: string) =>
    categoryData.value?.contents?.find(
      (section: { type: string }) => section.type === type,
    );

  const tipsSection = computed(() => findSection('tips-section'));
  const categoryCardsSection = computed(() =>
    findSection('category-cards-section'),
  );

  const handleChange = async ({
    categories,
    features,
    page,
  }: CatalogChange) => {
    router.push({
      query: {
        categories: categories?.length ? categories.join(',') : undefined,
        features: features?.length ? features.join(',') : undefined,
        page,
      },
    });

    fetchParams.page = page;
    fetchParams.category_id = categories;
    fetchParams.filters = features;

    await refresh();
  };

  return {
    chain,
    categoryData,
    products,
    totalPages,
    loadingProducts,
    tipsSection,
    categoryCardsSection,
    handleChange,
  };
}
